import { type GridColDef } from '@mui/x-data-grid-pro'
import clsx from 'clsx'
import { isProductTypeSpot } from '../../utils/exchange-utils'
import { isValidNumberString } from '../../utils/string-utils'
import {
  roundInteger,
  formatRoundedUsdAmount
} from '../../utils/rounding-formatting-utils'

const percentageFormatter = new Intl.NumberFormat('en-US',
  {
    style: 'percent',
  })

const BPS = 0.0001

export const MAIN_SUMMARY_COLUMNS: GridColDef[] = [
  {
    field: 'id',
    headerName: 'Symbol',
    width: 170,
    sortComparator: (v1, v2) => {
      const v1Splits = v1.split('_')
      const v2Splits = v2.split('_')
      if (v1Splits.length > v2Splits.length) {
        return 1
      } else if (v1Splits.length < v2Splits.length) {
        return -1
      }

      for (let i = 0; i < v1Splits.length; i++) {
        const localeCompareResult = v1Splits[i].localeCompare(v2Splits[i], undefined, { numeric: true })
        if (localeCompareResult !== 0) {
          return localeCompareResult
        }
      }
    },
    cellClassName: (params) => {
      return clsx('productType', {
        spot: isProductTypeSpot(params.value as string) ?? false,
        swap: !isProductTypeSpot(params.value as string) ?? false,
      })
    },
  },
  {
    field: 'Books.bbo--bid',
    headerName: 'BBO B',
    width: 85,
    align: 'left',
    valueGetter: (_value, row, _column, _apiRef) => {
      return row['Books.bbo']?.split('|')[0]?.trim()
    },
    cellClassName: (params) => {
      return clsx('isBestAggAskSymbol', {
        bold: params.row.isBestAggAskSymbol,
      })
    },
  },
  {
    field: 'Quoter.fundingRate',
    headerName: 'Funding',
    width: 50,
    align: 'right',
    cellClassName: (params) => {
      return clsx('fundingRate', {
        positive: isValidNumberString(params.value) && Number(params.value) >= 1,
        negative: isValidNumberString(params.value) && Number(params.value) <= -1,
      })
    }
  },
  {
    field: 'Risk.R',
    headerName: 'R (Coin)',
    width: 75,
    align: 'right',
  },
  {
    field: 'rUSD',
    headerName: 'Pos (USD)',
    width: 110,
    type: 'number',
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    valueGetter: (_value, row, _column, _apiRef) => {
      if (row['Risk.Pos'] !== undefined) {
        // Key exists
        return row['Risk.Pos']
      } else {
        // Key does not exist
        const rValue = row['Risk.R']
        const bboBid = row['Books.bbo']?.split('|')[0]?.trim()
        const bboAsk = row['Books.bbo']?.split('|')[1]?.trim()
        const midPrice = (Number(bboBid) + Number(bboAsk)) / 2
        return roundInteger(String(midPrice * Number(rValue)))
      }
    },
    cellClassName: (params) => {
      if (params.value === 0) {
        return ''
      }

      let buySideTradable = false
      let sellSideTradable = false

      for (const rowKey in params.row) {
        if (rowKey.startsWith('Tradability.') && rowKey.includes('tradable')) {
          const tradable: string = params.row[rowKey]
          const tradableSides = tradable.replaceAll(' ', '').split('|')
          buySideTradable = buySideTradable || tradableSides[0]?.toLowerCase() === 'true'
          sellSideTradable = sellSideTradable || tradableSides[1]?.toLowerCase() === 'true'
        }
      }

      return clsx('rUSD-tradability', {
        errorBuy: !buySideTradable,
        errorSell: !sellSideTradable,
      })
    },
  },
  {
    field: 'Risk.GR_pos',
    headerName: 'G. Net Pos (USD)',
    width: 110,
    type: 'number',
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      if (params.value === 0) {
        return ''
      }

      let buySideTradable = true
      let sellSideTradable = true

      for (const rowKey in params.row) {
        if (rowKey.startsWith('Risk.') && rowKey.includes('GR_pos_exceed')) {
          const tradable: string = params.row[rowKey]
          const tradableSides = tradable.replaceAll(' ', '').split('|')
          // can trade if limit not exceeded
          buySideTradable = tradableSides[0]?.toLowerCase() === 'false'
          sellSideTradable = tradableSides[1]?.toLowerCase() === 'false'
          break
        }
      }

      // display red if stuck
      return clsx('rUSD-tradability', {
        errorBuy: !buySideTradable || !sellSideTradable,
        errorSell: false,
      })
    },
  },
  {
    field: 'Risk.GR_abs_pos',
    headerName: 'G. Gross Pos (USD)',
    width: 110,
    type: 'number',
    valueFormatter: (value) => formatRoundedUsdAmount(value),
    cellClassName: (params) => {
      if (params.value === 0) {
        return ''
      }

      let buySideTradable = true
      let sellSideTradable = true

      for (const rowKey in params.row) {
        if (rowKey.startsWith('Risk.') && rowKey.includes('GR_abs_pos_exceed')) {
          const tradable: string = params.row[rowKey]
          const tradableSides = tradable.replaceAll(' ', '').split('|')
          // can trade if limit not exceeded
          buySideTradable = tradableSides[0]?.toLowerCase() === 'false'
          sellSideTradable = tradableSides[1]?.toLowerCase() === 'false'
          break
        }
      }

      // display red if stuck
      return clsx('rUSD-tradability', {
        errorBuy: !buySideTradable || !sellSideTradable,
        errorSell: false,
      })
    },
  },
  {
    field: 'Risk.V_Qp',
    headerName: 'V_Qp',
    width: 60,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const vQp = row['Risk.V_Qp']
      return (vQp == null || Number(vQp) === 0) ? '' : vQp
    },
  },
  {
    field: 'Quoter.bidSpread',
    headerName: 'Spread B',
    width: 70,
    align: 'right',
  },
  {
    field: 'Quoter.tgxBid',
    headerName: 'TGX B',
    width: 80,
    align: 'right',
    cellClassName: (params) => {
      const tgxB = Number(params.row['Quoter.tgxBid'])
      const bboB = Number(params.row['Books.bbo']?.split('|')[0]?.trim())
      return clsx('tgxBid', {
        mark: tgxB > bboB,
      })
    },
  },
  {
    field: 'Quoter.l0BidDistBps',
    headerName: 'L0 B',
    width: 65,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const l0BidPx = Number(row['Quoter.l0BidPx'])
      const l0BidDistBps = Number(row['Quoter.l0BidDistBps'])
      return l0BidPx > 0 && Number.isFinite(l0BidDistBps) ? row['Quoter.l0BidDistBps'] : ''
    },
  },
  {
    field: 'hidden-tgxBBps',
    headerName: 'TGX B (bps)',
    width: 80,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const tgxB = Number(row['Quoter.tgxBid'])
      const bboB = Number(row['Books.bbo']?.split('|')[0]?.trim())
      return tgxB > 0 && bboB > 0 ? (bboB - tgxB) / bboB / BPS : ''
    },
  },
  {
    field: 'Quoter.adjustment',
    headerName: 'Adjustment',
    width: 120,
    valueGetter: (_value, row, _column, _apiRef) => {
      if (row != null) {
        const sumAdj: number | string =
          row['Quoter.sumAdj'] == null || Number.isNaN(row['Quoter.sumAdj'])
            ? 'no-sumAdj-data'
            : row['Quoter.sumAdj']
        const shiftAdj: number | string =
          row['Quoter.shiftAdj'] == null || Number.isNaN(row['Quoter.shiftAdj'])
            ? 'no-shiftAdj-data'
            : row['Quoter.shiftAdj']
        const histAdj: number | string =
          row['Quoter.histAdj'] == null || Number.isNaN(row['Quoter.histAdj'])
            ? 'no-histAdj-data'
            : row['Quoter.histAdj']
        const fundingAdj: number | string =
          row['Quoter.fundingAdj'] == null || Number.isNaN(row['Quoter.fundingAdj'])
            ? 'no-fundingAdj-data'
            : row['Quoter.fundingAdj']
        const stableAdj: number | string =
          row['Quoter.stableAdj'] == null || Number.isNaN(row['Quoter.stableAdj'])
            ? 'no-stableAdj-data'
            : row['Quoter.stableAdj']
        const gShiftAdj: number | string =
          row['Quoter.gShiftAdj'] == null || Number.isNaN(row['Quoter.gShiftAdj'])
            ? 'no-gShiftAdj-data'
            : row['Quoter.gShiftAdj']

        return `T: ${sumAdj}\nS: ${shiftAdj}\nH: ${histAdj}\nF: ${fundingAdj}\nU: ${stableAdj}\nX: ${gShiftAdj}`
      }
    },
  },
  {
    field: 'hidden-tgxABps',
    headerName: 'TGX A (bps)',
    width: 80,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const tgxA = Number(row['Quoter.tgxAsk'])
      const bboA = Number(row['Books.bbo']?.split('|')[1]?.trim())
      return tgxA > 0 && bboA > 0 ? (tgxA - bboA) / bboA / BPS : ''
    },
  },
  {
    field: 'Books.bbo--ask',
    headerName: 'BBO A',
    width: 85,
    align: 'left',
    valueGetter: (_value, row, _column, _apiRef) => {
      return row['Books.bbo']?.split('|')[1]?.trim()
    },
    cellClassName: (params) => {
      return clsx('isBestAggBidSymbol', {
        bold: params.row.isBestAggBidSymbol,
      })
    },
  },
  {
    field: 'Quoter.tgxAsk',
    headerName: 'TGX A',
    width: 80,
    align: 'right',
    cellClassName: (params) => {
      const tgxA = Number(params.row['Quoter.tgxAsk'])
      const bboA = Number(params.row['Books.bbo']?.split('|')[1]?.trim())
      return clsx('tgxAsk', {
        mark: tgxA < bboA,
      })
    },
  },
  {
    field: 'Quoter.l0AskDistBps',
    headerName: 'L0 A',
    width: 65,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const l0AskPx = Number(row['Quoter.l0AskPx'])
      const l0AskDistBps = Number(row['Quoter.l0AskDistBps'])
      return l0AskPx > 0 && Number.isFinite(l0AskDistBps) ? row['Quoter.l0AskDistBps'] : ''
    },
  },
  {
    field: 'Quoter.askSpread',
    headerName: 'Spread A',
    width: 70,
    align: 'right',
  },
  {
    field: 'Risk.V_Qn',
    headerName: 'V_Qn',
    width: 60,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const vQn = row['Risk.V_Qn']
      return (vQn == null || Number(vQn) === 0) ? '' : vQn
    },
  },
  {
    field: 'Tradability.MR',
    headerName: 'MR (min to 0)',
    width: 90,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      let closestToZeroTradabilityKey: string | null = null
      let closestToZeroTradabilityAbsMR: number = 0
      let closestToZeroTradabilityMR: number = 0

      for (const rowKey in row) {
        if (rowKey.startsWith('Tradability.') && rowKey.includes('margin_ratio')) {
          const MR = row[rowKey]
          if (MR === '-') {
            continue
          }

          const absMR = Math.abs(MR)

          if (closestToZeroTradabilityKey == null) {
            closestToZeroTradabilityKey = rowKey
            closestToZeroTradabilityAbsMR = absMR
            closestToZeroTradabilityMR = MR
            continue
          }

          if (absMR < closestToZeroTradabilityAbsMR) {
            closestToZeroTradabilityKey = rowKey
            closestToZeroTradabilityAbsMR = absMR
            closestToZeroTradabilityMR = MR
          }
        }
      }

      if (closestToZeroTradabilityKey == null || closestToZeroTradabilityKey.length === 0) {
        return ''
      } else {
        return `${closestToZeroTradabilityMR} (${closestToZeroTradabilityKey.split('.')[1]})`
      }
    },
  },
  {
    field: 'Tradability.tradable',
    headerName: 'tradable',
    width: 80,
    valueGetter: (_value, row, _column, _apiRef) => {
      const tradableKeyValuePairs: string[] = []

      for (const rowKey in row) {
        if (rowKey.startsWith('Tradability.') && rowKey.includes('tradable')) {
          const tradable: string = row[rowKey]
          tradableKeyValuePairs.push(`${rowKey.split('.')[1]}: ${tradable}`)
        }
      }

      return tradableKeyValuePairs.join('\n')
    },
  },
  {
    field: 'Quoter.vr',
    headerName: 'VR',
    width: 50,
    align: 'right',
    valueGetter: (_value, row, _column, _apiRef) => {
      const volumeRatio = row['Quoter.vr']
      return isValidNumberString(volumeRatio) ? percentageFormatter.format(volumeRatio) : ''
    },
  },
  {
    field: 'Risk.PredFunding',
    headerName: 'P.Fund.',
    width: 50,
    align: 'right',
  },
  {
    field: 'hidden-init_pos',
    headerName: 'init_pos',
    type: 'number',
    valueGetter: (_value, row, _column, _apiRef) => {
      const rValue = row['Risk.R']
      const bboBid = row['Books.bbo']?.split('|')[0]?.trim()
      const bboAsk = row['Books.bbo']?.split('|')[1]?.trim()
      const midPrice = (Number(bboBid) + Number(bboAsk)) / 2
      return midPrice * Number(rValue)
    },
  },
]
